import type { LayerKey } from '@festina-life/api'
import { dateUtils } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import { useMemo } from 'react'
import * as Yup from 'yup'

import { useMetaModelContext } from '../../context/metaModelContext'
import { useTranslateLayer } from '../../hooks/useTranslateLayer'
import { useNavigationParams } from '../../system/useNavigationParams'
import { templateModelTypeFromCreateOperation } from '../../uiModels/helpers'
import { getTemplateOperationFromDate } from '../../uiModels/templateOperations/helpers'
import {
  ALL_VERSIONS_VIEW_DATE,
  getApplicationViewTemplates,
} from '../../workspace/helpers'
import { useOperationsContext } from '../../workspace/operationsContext'
import { usePathSelectorValidations } from '../draftLocationSelectors/usePathSelectorValidations'
import type { CreateTemplateDraftInput } from './types'

export const useTemplateDraftValidationSchema = ({
  layer,
  block,
}: {
  layer: LayerKey
  block: string
}) => {
  const translate = useTranslate()
  const { templateOperations } = useOperationsContext()
  const pathSelectorValidations = usePathSelectorValidations()
  const { layerTopology, blockTopology } = useMetaModelContext()
  const translateLayer = useTranslateLayer()
  const { layer: currentLayer } = useNavigationParams()
  const templateOperationsForBlockAndLayer = useMemo(
    () =>
      getApplicationViewTemplates({
        templateOperations,
        layer,
        block,
        layerTopology,
        blockTopology,
        viewAt: ALL_VERSIONS_VIEW_DATE,
      }),
    [templateOperations, layer, block, layerTopology, blockTopology],
  )

  return Yup.object({
    isHideOperation: Yup.boolean(),
    modelTypeName: Yup.string().when('isHideOperation', {
      is: false,
      then: (schema) => schema.required(translate('validation.required')),
      otherwise: (schema) => schema.notRequired(),
    }),
    templateId: Yup.string()
      .required(translate('validation.required'))
      .min(2, translate('validation.length', { length: 2 }))
      .noSpaces(translate('validation.noSpaces'))
      .test(
        'validTemplateType',
        translate('validation.invalidType'),
        (value, context) => {
          const formInput = context.parent as Partial<CreateTemplateDraftInput>
          const currentModelTypeName = formInput.modelTypeName

          const matchingOperation = value
            ? templateOperationsForBlockAndLayer?.get(value)?.operations.at(0)
                ?.internalOperation
            : null

          const existentOperationModelTypeName = matchingOperation
            ? templateModelTypeFromCreateOperation(matchingOperation)
            : undefined

          return (
            context.parent.isHideOperation ||
            !matchingOperation ||
            !existentOperationModelTypeName ||
            existentOperationModelTypeName === currentModelTypeName
          )
        },
      )
      .test(
        'invalidTemplateId',
        translate('validation.duplicatedTemplateId'),
        (value, context) => {
          const formInput = context.parent as Partial<CreateTemplateDraftInput>
          const variant = formInput.variant

          const isCollidingWithExistingTemplate =
            value && templateOperationsForBlockAndLayer?.get(value)

          return variant !== 'version' ? !isCollidingWithExistingTemplate : true
        },
      )
      .test({
        message: translate(
          'configurationTree.pathSelector.validation.differentLayer',
          { layer: translateLayer(currentLayer) },
        ),
        test: (_value, context) => {
          const formInput = context.parent as Partial<CreateTemplateDraftInput>
          const operationType = formInput.variant

          return operationType === 'version' ? layer.equals(currentLayer) : true
        },
      }),
    fromDate: Yup.string()
      .test({
        test: (value) =>
          !value || !isNaN(dateUtils.dateUtcAsLocal(value!).valueOf()),
        message: translate('validation.invalidDate'),
      })
      .test(
        'nonExistentOperation',
        translate('validation.duplicatedOperation'),
        (value, context) => {
          const formInput = context.parent as Partial<CreateTemplateDraftInput>
          const currentTemplateId = formInput.templateId

          const matchingOperation = currentTemplateId
            ? templateOperationsForBlockAndLayer?.get(currentTemplateId)
            : undefined

          return !(matchingOperation?.operations ?? []).some(
            (entry) =>
              entry.isDraft &&
              getTemplateOperationFromDate(entry.internalOperation) == value,
            // Purposely using == to allow for undefined and null to be treated the same, since the fromDate is optional
          )
        },
      ),
    packagePath: pathSelectorValidations,
  })
}
